import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { ArrowChevronDownMiniIcon, ArrowChevronUpMiniIcon, AppsIcon, UserMultipleIcon, LinkIcon, CalendarIcon, ToggleOffIcon, InformationCircleMiniIcon } from '@calendly/ui/components/icons';
import { InlineBanner } from '@calendly/ui/components/inline-banner';
import { Item } from '@calendly/ui/components/menu';
import { BodyText } from '@calendly/ui/components/typography';
import { primaryColorL1, errorColor } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef, Fragment } from 'react';
import { ViewportDropdownMenu } from 'components/core/viewport_dropdown_menu';
import { EventTypeSelect } from 'features/meeting_intelligence/components/event_type_select';
import { useNotetakerSettingsContext } from 'features/meeting_intelligence/settings/notetaker_settings_provider';
import { useMeetingIntelligenceContext } from 'features/meeting_intelligence/settings/provider';
import { DropdownWrapper, DropdownErrorWrapper, TitleWrapper } from './styles';
const DROPDOWN_OPTIONS = [{
  value: 'ALL',
  icon: AppsIcon,
  labelKey: 'publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.dropdown_options.all'
}, {
  value: 'OUTSIDE_COMPANY',
  icon: UserMultipleIcon,
  labelKey: 'publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.dropdown_options.outside_company'
}, {
  value: 'VIA_CALENDLY',
  icon: LinkIcon,
  labelKey: 'publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.dropdown_options.via_calendly'
}, {
  value: 'VIA_CALENDAR',
  icon: CalendarIcon,
  labelKey: 'publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.dropdown_options.via_calendar'
}, {
  value: 'MANUAL',
  icon: ToggleOffIcon,
  labelKey: 'publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.dropdown_options.manual',
  dividerAbove: true
}];
const inlineDropdownTextCss = "igc4xvu";
const InlineDropdownText = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(inlineDropdownTextCss, props.className)} />;
});
const dropdownContainerCss = "doy6yto";
const DropdownContainer = forwardRef((props, ref) => <span ref={ref} {...props} className={classNames(dropdownContainerCss, props.className)} />);
const textContainerCss = "tkvpidb";
const TextContainer = forwardRef((props, ref) => <div ref={ref} {...props} className={classNames(textContainerCss, props.className)} />);
const bareButtonCss = "b1hivdip";
const StyledBareButton = forwardRef((props, ref) => <BareButton ref={ref} {...props} className={classNames(bareButtonCss, props.className)} />);
const iconCss = "iwfqo4t";
const eventTypeDropdownSpacingCss = "e1exdwzm";
const StyledIcon = forwardRef((props, ref) => <span ref={ref} {...props} className={classNames(iconCss, props.className)} />);
const dividerCss = "dnqcyze";
const Divider = () => <hr className={dividerCss} />;
const recordingInfoContainerCss = "rv9m1d7";
const truncatedEmailCss = "t1asfjxw";
const RecordingInfoContainer = forwardRef((props, ref) => <span ref={ref} {...props} className={classNames(recordingInfoContainerCss, props.className)} />);
export const RecordingDefaultsBody = () => {
  const {
    recordingDefaultOption,
    setRecordingDefaultOption,
    eventTypes,
    hasEventError,
    actualSelectedCount,
    handleEventTypeSelect,
    handleSelectAll,
    handleClearAll,
    handleFetchMoreEvents
  } = useNotetakerSettingsContext();
  const {
    email,
    isValidCalendar
  } = useMeetingIntelligenceContext() || {};
  const [toggleDropdown, Dropdown, isExpanded] = useDropdown();
  const calendarEmail = email || I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.dropdown_options.via_calendar_default');
  const allInfoCalendarEmail = email || I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.all_info_default');
  const selectedOption = DROPDOWN_OPTIONS.find(opt => opt.value === recordingDefaultOption);
  const dropdownOptionLabel = selectedOption ? I18n.t(selectedOption.labelKey, selectedOption.value === 'VIA_CALENDAR' ? {
    calendar_email: calendarEmail
  } : undefined) : '';
  const handleOptionClick = value => {
    setRecordingDefaultOption(value);
    toggleDropdown();
  };
  return <div>
      <TitleWrapper>
        <BodyText size="medium" weight="bold">
          {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.title')}
        </BodyText>
        <BodyText size="medium" color="muted">
          {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.description')}
        </BodyText>
      </TitleWrapper>

      <TextContainer>
        {recordingDefaultOption === 'ERROR' ? <InlineBanner title={I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.update_settings_banner.title')} variant="neutral" isDismissible={false} message={I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.update_settings_banner.message')} primaryButton={{
        decoration: 'secondary-outline',
        label: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.update_settings_banner.button_label'),
        onClick: () => setRecordingDefaultOption('VIA_CALENDLY')
      }} /> : <>
            <BodyText size="medium" as="span">
              {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.notetaker_should_join')}
              {' '}
            </BodyText>
            <DropdownContainer>
              <StyledBareButton onClick={toggleDropdown}>
                <InlineDropdownText>
                  {dropdownOptionLabel}
                </InlineDropdownText>
                <StyledIcon>
                  {isExpanded ? <ArrowChevronUpMiniIcon color={primaryColorL1} /> : <ArrowChevronDownMiniIcon color={primaryColorL1} />}
                </StyledIcon>
              </StyledBareButton>
              <Dropdown>
                <ViewportDropdownMenu pullRight>
                  {DROPDOWN_OPTIONS
              // filter out VIA_CALENDAR if no valid calendar connection
              .filter(option => option.value !== 'VIA_CALENDAR' || isValidCalendar)
              // render items
              .map(option => {
                const Icon = option.icon;
                return <Fragment key={option.value}>
                          {option.dividerAbove && <Divider />}
                          <Item onClick={() => handleOptionClick(option.value)}>
                            {Icon && <Icon />}
                            {I18n.t(option.labelKey, option.value === 'VIA_CALENDAR' ? {
                      calendar_email: calendarEmail
                    } : undefined)}
                          </Item>
                        </Fragment>;
              })}
                </ViewportDropdownMenu>
              </Dropdown>
            </DropdownContainer>
          </>}
      </TextContainer>
      {recordingDefaultOption === 'ALL' && isValidCalendar && <RecordingInfoContainer>
        <InformationCircleMiniIcon />
        <BodyText size="small">
          {email ? <>
              {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.all_info_prefix')}
              {' '}
              <span className={truncatedEmailCss} title={email}>{email}</span>
              .
            </> : I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.all_info', {
          calendar_email: allInfoCalendarEmail
        })}
        </BodyText>
      </RecordingInfoContainer>}
      {recordingDefaultOption === 'OUTSIDE_COMPANY' && <RecordingInfoContainer>
          <InformationCircleMiniIcon />
          <BodyText size="small">
            {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.outside_company_info')}
          </BodyText>
        </RecordingInfoContainer>}
      {recordingDefaultOption === 'VIA_CALENDLY' && <div>
          <div className={eventTypeDropdownSpacingCss}>
            <DropdownWrapper>
              <EventTypeSelect placeholder={I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.no_event_types_placeholder')} disabled={false} eventTypes={eventTypes} onChange={handleEventTypeSelect} handleSelectAll={handleSelectAll} handleClearAll={handleClearAll} handleFetchMoreEvents={handleFetchMoreEvents} actualSelectedCount={actualSelectedCount} isWide />
              {hasEventError && <DropdownErrorWrapper>
                  <BodyText color={errorColor} size="medium">
                    {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.dropdown_error_text')}
                  </BodyText>
                </DropdownErrorWrapper>}
            </DropdownWrapper>
          </div>
        </div>}
    </div>;
};

require("./recording_defaults_body.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./recording_defaults_body.js");